import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  signingSessionDtoSchema,
  type SigningSessionDto,
} from '@lib/dto/signing-session.dto'
import {
  qaSessionDtoSchema,
  questionThreadDtoSchema,
  type CreateQuestionInput,
  type QaSessionDto,
  type QuestionThreadDto,
  type ReplyQuestionInput,
} from '@lib/dto/question.dto'
import type {
  AcceptConsentInput,
  CompleteSigningInput,
  DeclineSigningInput,
  RequestPaperCopyInput,
  SaveSigningDraftInput,
} from '@lib/input/signing.input'
import { CONSENT_VERSION } from '@lib/constants/envelope'
import type { DocumentSourceRoot, ISignService } from '@/types/SignService.types'

class SignService implements ISignService {
  async getSession(token: string): Promise<SigningSessionDto> {
    try {
      return await api.get(
        `/v1/sign/${encodeURIComponent(token)}`,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to load signing session', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async acceptConsent(
    token: string,
    input: AcceptConsentInput,
  ): Promise<SigningSessionDto> {
    try {
      return await api.post(
        `/v1/sign/${encodeURIComponent(token)}/consent`,
        input,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to accept consent', { error: toErrorMessage(error) })
      throw error
    }
  }

  async saveDraft(
    token: string,
    input: SaveSigningDraftInput,
  ): Promise<SigningSessionDto> {
    try {
      return await api.post(
        `/v1/sign/${encodeURIComponent(token)}/draft`,
        input,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to save signing draft', { error: toErrorMessage(error) })
      throw error
    }
  }

  async complete(
    token: string,
    input: CompleteSigningInput,
  ): Promise<SigningSessionDto> {
    try {
      return await api.post(
        `/v1/sign/${encodeURIComponent(token)}/complete`,
        input,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to complete signing', { error: toErrorMessage(error) })
      throw error
    }
  }

  async decline(
    token: string,
    input: DeclineSigningInput,
  ): Promise<SigningSessionDto> {
    try {
      return await api.post(
        `/v1/sign/${encodeURIComponent(token)}/decline`,
        input,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to decline signing', { error: toErrorMessage(error) })
      throw error
    }
  }

  async requestPaperCopy(
    token: string,
    input: RequestPaperCopyInput,
  ): Promise<SigningSessionDto> {
    try {
      return await api.post(
        `/v1/sign/${encodeURIComponent(token)}/paper-copy`,
        input,
        signingSessionDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to request a paper copy', { error: toErrorMessage(error) })
      throw error
    }
  }

  async getDocument(
    token: string,
    documentId: string,
    source: DocumentSourceRoot = 'sign',
  ): Promise<Blob> {
    try {
      return await api.getBlob(
        `/v1/${source}/${encodeURIComponent(token)}/documents/${encodeURIComponent(documentId)}`,
      )
    } catch (error) {
      logger.error('Failed to load signing document', {
        documentId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getUnsignedPacket(token: string): Promise<Blob> {
    try {
      return await api.getBlob(
        `/v1/sign/${encodeURIComponent(token)}/unsigned-packet`,
      )
    } catch (error) {
      logger.error('Failed to download unsigned packet', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getSignedPacket(token: string): Promise<Blob> {
    try {
      return await api.getBlob(`/v1/sign/${encodeURIComponent(token)}/packet`)
    } catch (error) {
      logger.error('Failed to download signed packet', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getCertificate(token: string): Promise<Blob> {
    try {
      return await api.getBlob(`/v1/sign/${encodeURIComponent(token)}/certificate`)
    } catch (error) {
      logger.error('Failed to download certificate', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async getAsset(
    token: string,
    documentId: string,
    assetId: string,
    source: DocumentSourceRoot = 'sign',
  ): Promise<Blob> {
    try {
      return await api.getBlob(
        `/v1/${source}/${encodeURIComponent(token)}/documents/${encodeURIComponent(documentId)}/assets/${encodeURIComponent(assetId)}`,
      )
    } catch (error) {
      logger.error('Failed to load signing asset', {
        documentId,
        assetId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async createQuestion(
    token: string,
    input: CreateQuestionInput,
  ): Promise<QuestionThreadDto> {
    return api.post(
      `/v1/sign/${encodeURIComponent(token)}/questions`,
      input,
      questionThreadDtoSchema,
    )
  }

  async replyQuestion(
    token: string,
    threadId: string,
    input: ReplyQuestionInput,
    source: DocumentSourceRoot = 'sign',
  ): Promise<QuestionThreadDto> {
    return api.post(
      `/v1/${source}/${encodeURIComponent(token)}/questions/${encodeURIComponent(threadId)}/replies`,
      input,
      questionThreadDtoSchema,
    )
  }

  async touchPresence(token: string, source: DocumentSourceRoot = 'sign'): Promise<void> {
    await api.postEmpty(`/v1/${source}/${encodeURIComponent(token)}/presence`)
  }

  async getQaSession(token: string): Promise<QaSessionDto> {
    return api.get(`/v1/qa/${encodeURIComponent(token)}`, qaSessionDtoSchema)
  }

  async acceptQaConsent(token: string): Promise<QaSessionDto> {
    return api.post(
      `/v1/qa/${encodeURIComponent(token)}/consent`,
      { version: CONSENT_VERSION },
      qaSessionDtoSchema,
    )
  }
}

export const signService = new SignService()
